import {ofType} from 'redux-observable';
import {concat, iif, of} from 'rxjs';
import {ajax} from 'rxjs/ajax';
import {catchError, debounceTime, delay, filter, mapTo, mergeMap, takeUntil, timeout} from 'rxjs/operators';
import {AnyAction} from 'redux';
import {processApiError} from '../../utils/ApiUtil';
import {addCredentialAndExecute} from '../../utils/RetryUtil';
import * as UIActions from '../../actions/UIActions';
import * as ValidationActions from '../../actions/ValidationActions';
import * as WizardActions from '../../actions/WizardActions';
import * as WizardMXActions from '../../actions/WizardMXActions';
import * as LoanApplicationActions from '../../actions/LoanApplicationActions';
import * as CatalogActions from '../../actions/CatalogActions';
import * as MC from '../../constants/MotorcreditConstants';
import * as ErrorConstants from '../../constants/ErrorConstants';
import {COUNTRIES} from '../../constants/AppConstants';
import {Catalog} from '../../../common/types/common';
import {CalculatorState} from '../../types/mx/creditoReal/reducers';
import {BankTarget} from '../../types/mx/types';
import * as CalculatorActions from '../../actions/CalculatorActions';
import {CatalogsState, State} from '../../types/mx/reducers';
import slugify from 'slugify';

export const getAllCatalogSearchMX = (action$, state$) =>
    action$.pipe(
        ofType(UIActions.INIT_APP_ACTION.name),
        filter((action: AnyAction) => COUNTRIES.MX.id === action.country),
        mergeMap((action: AnyAction) => {
            const url = `${MC.MCC_URL(action.country)}/catalogs?size=300`;
            return concat(
                addCredentialAndExecute(headers => ajax.get(url, headers), state$.value).pipe(
                    timeout(MC.TIMEOUT),
                    mergeMap(({response, refreshTokenObs}: any) => {
                        const configs = response.response.data.by_bank_general_config;
                        const creditoRealConfig = configs && configs.find(item =>
                            item.target === BankTarget.CREDITO_REAL) || {};

                        const turboCashConfig = configs && configs.find(item =>
                            item.target === BankTarget.TURBO_CASH) || {};

                        const  smsTimerResend = response.response.data.sms_timer_resend &&
                                                response.response.data.sms_timer_resend.length > 0 ?
                                                response.response.data.sms_timer_resend[0] : {};

                        const years: Catalog[] = response.response.data.year_terms || [];
                        const employmentTypes: Catalog[] = response.response.data.employment_relationship_types || [];

                        const catalogs: CatalogsState = state$.value.catalogs;
                        const calculator: CalculatorState = state$.value.creditoReal.calculator;
                        const provinceSelected: Catalog = response.response.data.province.find(m =>
                            m.payload.toUpperCase() === catalogs.queryStringProvince.toUpperCase()
                        ) || calculator.provinceSelected;
                        const queryStringYear = state$.value.catalogs.queryStringYear;

                        return concat(
                            of(refreshTokenObs),
                            of(CatalogActions.GOT_ALL_CATALOGS_SUCCESSFULLY.action()),
                            of(ValidationActions.GOT_MIN_ALLOWED_AGE_ACTION.action(
                                creditoRealConfig.minAllowAge, turboCashConfig.minAllowAge
                            )),
                            of(ValidationActions.GOT_MIN_ALLOWED_VEHICLE_PRICE_ACTION.action(
                                creditoRealConfig.minAllowVehiclePrice, turboCashConfig.minAllowVehiclePrice
                            )),
                            of(ValidationActions.GOT_MIN_DOWN_PAYMENT_ACTION.action(
                                creditoRealConfig.minDownPayment, turboCashConfig.minDownPayment
                            )),
                            of(ValidationActions.GOT_MAX_DOWN_PAYMENT_ACTION.action(
                                creditoRealConfig.maxDownPayment, turboCashConfig.maxDownPayment
                            )),
                            of(CatalogActions.GOT_YEARS_ACTION.action(
                                years, queryStringYear
                            )),
                            of(CatalogActions.GOT_EMPLOYMENT_TYPES_ACTION.action(
                                employmentTypes
                            )),
                            of(CatalogActions.GOT_PROVINCES_ACTION.action(
                                response.response.data.province, provinceSelected
                            )),
                            of(ValidationActions.GOT_SMS_TIMER_RESEND_ACTION.action(
                                smsTimerResend.seconds
                            )),
                            of(CatalogActions.GOT_BUREAU_CATALOG_ACTION.action(
                                response.response.data.bureau_catalogs
                            )),
                            of(CatalogActions.GOT_CHANNEL_REFERENTS_ACTION.action(
                                response.response.data.channel_referents
                            )),
                            of(CatalogActions.GOT_CIVIL_STATUS_ACTION.action(
                                response.response.data.civil_status
                            )),
                            of(CatalogActions.GOT_REGIME_ACTION.action(
                                response.response.data.regime
                            )),
                            of(CatalogActions.GOT_ROL_HOME_ACTION.action(
                                response.response.data.rol_home
                            )),
                            of(CatalogActions.GOT_STUDIES_ACTION.action(
                                response.response.data.studies
                            )),
                            of(CatalogActions.GOT_ASSEMBLY_COUNTRY_ACTION.action(
                                response.response.data.assembly_country
                            )),
                            of(CatalogActions.GOT_HOUSE_TYPE_ACTION.action(
                                response.response.data.house_type
                            )),
                            of(CatalogActions.GOT_LAPSE_ACTION.action(
                                response.response.data.lapse
                            )),
                            of(CatalogActions.GOT_EMPLOYMENT_ROTATION_ACTION.action(
                                response.response.data.employment_rotation
                            )),
                            of(CatalogActions.GOT_EMPLOYMENT_BILLING_TYPES_ACTION.action(
                                response.response.data.billing_types
                            )),
                            of(CatalogActions.GOT_EMPLOYMENT_TAX_REGIMES_ACTION.action(
                                response.response.data.tax_regimes
                            )),
                            of(ValidationActions.GOT_PRICE_AND_DOWN_PAYMENT_ACTION.action(
                                creditoRealConfig.minAllowVehiclePrice,
                                creditoRealConfig.minDownPayment,
                                creditoRealConfig.maxDownPayment
                            )),
                            of(CatalogActions.GOT_CR_GENDER_ACTION.action(
                                response.response.data.cr_gender
                            )),
                            of(CatalogActions.GOT_CR_CIVIL_STATUS_ACTION.action(
                                response.response.data.cr_civil_status
                            )),
                            of(CatalogActions.GOT_CR_STUDIES_LEVEL_ACTION.action(
                                response.response.data.cr_studies
                            )),
                            of(CatalogActions.GOT_CR_COUNTRIES_WITH_NATIONALITIES_ACTION.action(
                                response.response.data.cr_countries_with_nationalities,
                            )),
                            of(CatalogActions.GOT_CR_HOUSE_TYPE_ACTION.action(
                                response.response.data.cr_house_type
                            )),
                            of(CatalogActions.GOT_CR_DOCUMENT_TYPE_ACTION.action(
                                response.response.data.cr_document_type
                            )),
                            of(CatalogActions.GOT_CR_BUSINESS_TYPE_ACTION.action(
                                response.response.data.cr_business_type
                            )),
                        );
                    }),
                    catchError((error) => {
                        return processApiError([
                            UIActions.ERROR_GENERAL_ACTION.action(error, action, url, {}, 'GET')
                        ])(error);
                    })
                )
            );
        })
    );

export const searchBrandsMX = (action$, state$) =>
    action$.pipe(
        ofType(CalculatorActions.YEAR_SELECTED_ACTION.name, CatalogActions.GOT_YEARS_ACTION.name),
        debounceTime(1500),
        filter(action => COUNTRIES.MX.id === state$.value.countrySelector.countrySelected),
        filter((action: AnyAction) => state$.value.creditoReal.calculator.yearSelected.id.length === 4 &&
            !!!state$.value.creditoReal.ui.calculatorStepView.fieldErrors.yearFieldError),
        mergeMap((action: AnyAction) => {
            const state: State = state$.value;
            const catalogs: CatalogsState = state.catalogs;
            const calculator: CalculatorState = state.creditoReal.calculator;
            const countrySelected = state.countrySelector.countrySelected;
            const yearId = calculator.yearSelected.id;
            const url =  `${MC.MCC_URL(countrySelected)}/catalogs/years/${yearId}/brands?size=300`;
            return concat(
                of(UIActions.IS_LOADING_ACTION.action(CatalogActions.GOT_BRANDS_ACTION.name)),
                addCredentialAndExecute(headers => ajax.get(url, headers),
                    state$.value).pipe(
                    timeout(MC.TIMEOUT),
                    mergeMap(({response, refreshTokenObs}: any) => {
                        const brandSelected: Catalog = response.response.data.find(b => b.id.toUpperCase()
                            === catalogs.queryStringBrand.toUpperCase()) || calculator.brandSelected;
                        return concat(
                            of(refreshTokenObs),
                            of(UIActions.IS_LOADED_ACTION.action(CatalogActions.GOT_BRANDS_ACTION.name)),
                            of(CatalogActions.GOT_BRANDS_ACTION.action(response.response.data, brandSelected))
                        );
                    }),
                    catchError((error) => {
                        return processApiError([
                            UIActions.IS_LOADED_ACTION.action(CatalogActions.GOT_BRANDS_ACTION.name),
                            UIActions.ERROR_GENERAL_ACTION.action(error, action, url, {}, 'GET')
                        ])(error);
                    })
                )
            );
        }
    ));

export const searchModelsMX = (action$, state$) =>
    action$.pipe(
        ofType(CalculatorActions.BRAND_SELECTED_ACTION.name, CatalogActions.GOT_BRANDS_ACTION.name),
        filter(action => COUNTRIES.MX.id === state$.value.countrySelector.countrySelected),
        filter((action: AnyAction) => !!action.brand && action.brand.id),
        mergeMap((action: AnyAction) => {
            const state: State = state$.value;
            const catalogs: CatalogsState = state.catalogs;
            const calculator: CalculatorState = state.creditoReal.calculator;
            const countrySelected = state.countrySelector.countrySelected;
            const yearSelectedId = calculator.yearSelected.id;
            const brandId = action.brand.id;
            const url = `${MC.MCC_URL(countrySelected)}/catalogs/years/${yearSelectedId}/brands/${
                brandId}/models?size=300`;
            return concat(
                of(UIActions.IS_LOADING_ACTION.action(CatalogActions.GOT_MODELS_ACTION.name)),
                addCredentialAndExecute(headers => ajax.get(url, headers), state$.value).pipe(
                    timeout(MC.TIMEOUT),
                    mergeMap(({response, refreshTokenObs}: any) => {
                        const modelSelected: Catalog = response.response.data.find(m => m.id.toUpperCase()
                            === catalogs.queryStringModel.toUpperCase()) || calculator.modelSelected;
                        return concat(
                            of(refreshTokenObs),
                            of(UIActions.IS_LOADED_ACTION.action(CatalogActions.GOT_MODELS_ACTION.name)),
                            of(CatalogActions.GOT_MODELS_ACTION.action(response.response.data, modelSelected))
                        );
                    }),
                    catchError((error) => {
                        return processApiError([
                            UIActions.IS_LOADED_ACTION.action(CatalogActions.GOT_MODELS_ACTION.name),
                            UIActions.ERROR_GENERAL_ACTION.action(error, action, url, {}, 'GET')
                        ])(error);
                    })
                )
            );
        }
    ));

export const searchVersionsMX = (action$, state$) =>
    action$.pipe(
        ofType(CalculatorActions.MODEL_SELECTED_ACTION.name, CatalogActions.GOT_MODELS_ACTION.name),
        filter(action => COUNTRIES.MX.id === state$.value.countrySelector.countrySelected),
        filter((action: AnyAction) => !!action.model && action.model.id),
        mergeMap((action: AnyAction) => {
            const catalogs: CatalogsState = state$.value.catalogs;
            const calculator: CalculatorState = state$.value.creditoReal.calculator;
            const countrySelected = state$.value.countrySelector.countrySelected;
            const yearSelectedId = state$.value.creditoReal.calculator.yearSelected.id;
            const brandSelectedId = state$.value.creditoReal.calculator.brandSelected.id;
            const modelId = action.model.id;
            // tslint:disable-next-line: max-line-length
            const url = `${MC.MCC_URL(countrySelected)}/catalogs/years/${yearSelectedId}/brands/${brandSelectedId}/models/${modelId}/versions?size=300`;
            return concat(
                of(UIActions.IS_LOADING_ACTION.action(CatalogActions.GOT_VERSIONS_ACTION.name)),
                addCredentialAndExecute(headers => ajax.get(url, headers), state$.value).pipe(
                    timeout(MC.TIMEOUT),
                    mergeMap(({response, refreshTokenObs}: any) => {
                        const versionSelected: Catalog = response.response.data.find(m => slugify(m.id).toLowerCase()
                            === catalogs.queryStringVersion) || calculator.versionSelected;
                        return concat(
                            of(refreshTokenObs),
                            of(UIActions.IS_LOADED_ACTION.action(CatalogActions.GOT_VERSIONS_ACTION.name)),
                            of(CatalogActions.GOT_VERSIONS_ACTION.action(response.response.data, versionSelected))
                        );
                    }),
                    catchError((error) => {
                        return processApiError([
                            UIActions.IS_LOADED_ACTION.action(CatalogActions.GOT_VERSIONS_ACTION.name),
                            UIActions.ERROR_GENERAL_ACTION.action(error, action, url, {}, 'GET')
                        ])(error);
                    })
                )
            );
        }
    ));

export const getAllQuotationInsurances = (action$, state$) =>
    action$.pipe(
        ofType(LoanApplicationActions.GET_ALL_QUOTATION_INSURANCES_ACTION.name),
        mergeMap((action: AnyAction) => {
            const url = `${MC.MCC_URL(COUNTRIES.MX.id)}/insurances/quotation-all-insurances`;
            const body = {
                year: parseInt(action.year, 10),
                postalCode: action.postalCode,
                cveClick: action.cveClick,
                term: {
                    id: `${action.term}`,
                    value: `${action.term}`
                }
            };
            console.log('----- LoanApplicationActions.GET_ALL_QUOTATION_INSURANCES -----', url, body);
            return concat(
                addCredentialAndExecute(headers => ajax.post(url, body, headers), state$.value).pipe(
                    timeout(MC.TIMEOUT),
                    mergeMap(({response, refreshTokenObs}: any) => {
                        console.log('----- LoanApplicationActions.GET_ALL_QUOTATION_INSURANCES SUCCESS -----',
                            response.response.data);
                        return concat(
                            of(refreshTokenObs),
                            iif(
                                () => response.response.data.length === 0,
                                concat(
                                    of(WizardMXActions.GOT_INSURANCE_ERROR_ACTION.action()),
                                    of(WizardMXActions.GOT_API_ERROR_ACTION.action(
                                        ErrorConstants.WSDL_BITUAJ_ERROR))
                                ),
                                of(CatalogActions.GOT_ALL_QUOTATION_INSURANCES_ACTION.action(
                                    response.response.data))
                            )
                        );
                    }),
                    catchError((error) => {
                        const errorIsHasBituajError = Array.isArray(error) ?
                            error.includes(ErrorConstants.WSDL_BITUAJ_ERROR) : false;
                        return concat(
                            iif(
                                () => errorIsHasBituajError,
                                concat(
                                    of(WizardMXActions.GOT_API_ERROR_ACTION.action(ErrorConstants.WSDL_BITUAJ_ERROR)),
                                    of(WizardActions.CHANGE_INCLUDE_INSURANCE_ACTION.action(!errorIsHasBituajError))
                                ),
                                of(UIActions.ERROR_GENERAL_ACTION.action(error, action, url, body, 'POST'))
                            )
                        );
                    }),
                    takeUntil(action$.pipe(
                        ofType(LoanApplicationActions.GET_ALL_QUOTATION_INSURANCES_ACTION_CANCELLED.name)))
                ));
        }
    ));

export const onChangeIncludeInsurance = (action$, state$) =>
    action$.pipe(
        ofType(WizardActions.CHANGE_INCLUDE_INSURANCE_ACTION.name),
        delay(500),
        filter((action: AnyAction) => action.includeInsurance),
        mergeMap(action => of(LoanApplicationActions.GET_ALL_QUOTATION_INSURANCES_ACTION.action(
            state$.value.creditoReal.calculator.yearSelected.value,
            state$.value.creditoReal.loanApplication.applicantHome.postalCode,
            state$.value.creditoReal.calculator.versionSelected.payload,
            state$.value.creditoReal.calculator.monthlyPayment.id,
            true
        )))
    );

export const onChangeIncludeInsuranceToFalse = action$ =>
    action$.pipe(
        ofType(WizardActions.CHANGE_INCLUDE_INSURANCE_ACTION.name),
        filter((action: AnyAction) => !action.includeInsurance),
        mapTo(LoanApplicationActions.GET_ALL_QUOTATION_INSURANCES_ACTION_CANCELLED.action())
    );

export const getAllCatalogGraphCmsMX = action$ =>
    action$.pipe(
        ofType(UIActions.INIT_APP_ACTION.name),
        filter((action: AnyAction) => COUNTRIES.MX.id === action.country),
        mergeMap((action: AnyAction) => {
            const url = `${MC.MCC_URL(action.country)}/headless/catalog-graphcms`;
            return concat(
                ajax.get(url).pipe(
                    timeout(MC.TIMEOUT),
                    mergeMap((response: any) => {
                        const offers = response.response.data.offers;
                        const wizard = response.response.data.wizard;
                        return concat(
                            of(CatalogActions.GOT_OFFERS_ACTION.action(offers.map(item => ({
                                source: item.source,
                                title: item.title,
                                descriptionHtml: item.description?.html,
                                descriptionText: item.description?.text,
                                markdown: item.description?.markdown,
                                descriptionStyledHtml: item.descriptionStyled?.html,
                                descriptionStyledText: item.descriptionStyled?.text,
                                markdownStyled: item.descriptionStyled?.markdown
                            })))),
                            of(CatalogActions.GOT_WIZARD_ACTION.action(wizard.map(wizardMx => ({
                                step: wizardMx.step,
                                title: wizardMx.title,
                                descriptionHtml: wizardMx.description?.html,
                                descriptionMarkdown: wizardMx.description?.markdown,
                                descriptionText: wizardMx.description?.text,
                                informationMxes: wizardMx.informationMxes.map(informationMx => ({
                                    level: informationMx.level,
                                    title: informationMx.title,
                                    descriptionHtml: informationMx.description?.html,
                                    descriptionMarkdown: informationMx.description?.markdown,
                                    descriptionText: informationMx.description?.text,
                                    assistantMxes: informationMx.assistantMxes.map(assistantMx => ({
                                        assistantFileName: assistantMx.assistant?.fileName,
                                        assistantUrl: assistantMx.assistant?.url,
                                        assistantNameHtml: assistantMx.assistantName?.html,
                                        assistantNameMarkdown: assistantMx.assistantName?.markdown,
                                        assistantNameText: assistantMx.assistantName?.text,
                                        assistantChargeHtml: assistantMx.assistantCharge?.html,
                                        assistantChargeMarkdown: assistantMx.assistantCharge?.markdown,
                                        assistantChargeText: assistantMx.assistantCharge?.text
                                    }))
                                }))
                            }))))
                        );
                    }),
                    catchError((error) => {
                        return processApiError([
                            UIActions.ERROR_GENERAL_ACTION.action(error, action, url, {}, 'GET')
                        ])(error);
                    })
                )
            );
        })
    );
